import React from "react";
import ReactDOM from "react-dom";
import { Input as AntInput } from "antd";
import type { InputProps as AntInputProps, InputRef } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { ChevronDown, Search, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import {
  Tooltip,
  type TooltipPlacement,
  type TooltipVariant,
} from "../tool-tip";

const { Password, TextArea } = AntInput;

// ============================================
// Types
// ============================================

export type InputSize = "small" | "medium" | "large";
export type InputVariant = "outlined" | "filled" | "borderless";

export interface InputProps extends Omit<AntInputProps, "size"> {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
  type?: "text" | "password" | "email" | "number" | "tel" | "url";
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  allowClear?: boolean;
  className?: string;
  /** Tooltip content for label */
  tooltip?: React.ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: TooltipPlacement;
  /** Tooltip variant */
  tooltipVariant?: TooltipVariant;
}

export interface PasswordInputProps extends Omit<InputProps, "type"> {
  visibilityToggle?: boolean;
}

export interface TextAreaProps {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  rows?: number;
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  maxLength?: number;
  showCount?: boolean;
  /** Position the character count inside the textarea box (bottom-right) */
  countInside?: boolean;
  className?: string;
  id?: string;
  /** Whether to hide the border (useful when wrapping in a custom container) */
  borderless?: boolean;
  /** Content rendered after the textarea (e.g. character count) */
  suffix?: React.ReactNode;
  /** Custom wrapper style */
  wrapperStyle?: React.CSSProperties;
  /** Custom wrapper className */
  wrapperClassName?: string;
}

export interface PhoneInputCountry {
  code: string;
  dial_code: string;
  name: string;
  flag?: string;
}

export interface PhoneInputProps {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  countryCode?: string;
  defaultCountryCode?: string;
  onChange?: (value: string) => void;
  onCountryChange?: (country: PhoneInputCountry) => void;
  countries: PhoneInputCountry[];
  className?: string;
  searchable?: boolean;
  renderFlag?: (countryCode: string) => React.ReactNode;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
}

// ============================================
// Size Configuration
// ============================================
const sizeConfig = {
  small: { height: "2.5rem", fontSize: "0.875rem", px: "px-3" },
  medium: { height: "2.5rem", fontSize: "0.9375rem", px: "px-3" },
  large: { height: "3rem", fontSize: "1rem", px: "px-4" },
} as const;

const FIELD_TRANSITION = "border-color 0.15s ease, background-color 0.15s ease";
const getInlineAffixWidth = (affix?: React.ReactNode): number => {
  if (!affix) return 0;
  if (typeof affix === "string" || typeof affix === "number") {
    return String(affix).length * 8 + 12;
  }

  return 24;
};
const VARIABLE_TOKEN_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;
const ZERO_WIDTH_SPACE = "\u200B";

const getStringValue = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const useStringFieldValue = (value?: string, defaultValue?: string) => {
  const [internalValue, setInternalValue] = React.useState(
    getStringValue(defaultValue),
  );
  const currentStringValue = getStringValue(
    value !== undefined ? value : internalValue,
  );

  const setIfUncontrolled = React.useCallback(
    (nextValue: string) => {
      if (value === undefined) {
        setInternalValue(nextValue);
      }
    },
    [value],
  );

  return {
    currentStringValue,
    isFilled: currentStringValue.length > 0,
    setIfUncontrolled,
  };
};

interface FieldStyleOptions {
  size: InputSize;
  variant?: InputVariant;
  isActive: boolean;
  isFilled: boolean;
  disabled?: boolean;
  hasError?: boolean;
  borderless?: boolean;
  includeHeight?: boolean;
  style?: React.CSSProperties;
}

const getFieldBorderColor = ({
  borderless,
  hasError,
  isActive,
  isFilled,
}: Pick<
  FieldStyleOptions,
  "borderless" | "hasError" | "isActive" | "isFilled"
>): string => {
  if (borderless) return "transparent";
  if (hasError) return colors.error;
  if (isActive) return colors.primary;
  if (isFilled) return colors.surfaceDark;
  return colors.surface;
};

const getFieldBackgroundColor = ({
  disabled,
  isActive,
  variant = "filled",
}: Pick<
  FieldStyleOptions,
  "disabled" | "isActive" | "variant"
>): string => {
  if (disabled) return colors.surfaceLighter;
  if (isActive) return colors.white;
  return variant === "filled" ? colors.surfaceLight : colors.white;
};

const createFieldStyle = ({
  size,
  variant = "filled",
  isActive,
  isFilled,
  disabled = false,
  hasError = false,
  borderless = variant === "borderless",
  includeHeight = true,
  style,
}: FieldStyleOptions): React.CSSProperties => ({
  ...(includeHeight ? { height: sizeConfig[size].height } : {}),
  fontSize: sizeConfig[size].fontSize,
  borderColor: getFieldBorderColor({
    borderless,
    hasError,
    isActive,
    isFilled,
  }),
  borderWidth: borderless ? 0 : isActive || hasError ? 2 : 1,
  backgroundColor: borderless
    ? "transparent"
    : getFieldBackgroundColor({ disabled, isActive, variant }),
  borderRadius: 3,
  boxShadow: "none",
  transition: FIELD_TRANSITION,
  ...style,
});

const hasVariableToken = (value: string): boolean => {
  VARIABLE_TOKEN_PATTERN.lastIndex = 0;
  return VARIABLE_TOKEN_PATTERN.test(value);
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const variableChipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  maxWidth: "100%",
  padding: "1px 6px",
  borderRadius: 4,
  backgroundColor: colors.infoBgSubtle,
  color: colors.primaryDarkest,
  fontWeight: 500,
  fontSize: "0.9em",
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
};

const variableChipStyleString = Object.entries(variableChipStyle)
  .map(([key, value]) => {
    const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    return `${cssKey}:${value}`;
  })
  .join(";");

const valueToChipHtml = (value: string): string => {
  VARIABLE_TOKEN_PATTERN.lastIndex = 0;
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = VARIABLE_TOKEN_PATTERN.exec(value)) !== null) {
    const [token, rawVariableName] = match;
    if (match.index > lastIndex) {
      parts.push(escapeHtml(value.slice(lastIndex, match.index)));
    }

    const variableName = rawVariableName.trim();
    parts.push(
      `${ZERO_WIDTH_SPACE}<span data-var="${escapeHtml(variableName)}" contenteditable="false" style="${variableChipStyleString}">${escapeHtml(variableName || token)}</span>${ZERO_WIDTH_SPACE}`,
    );

    lastIndex = match.index + token.length;
  }

  if (lastIndex < value.length) {
    parts.push(escapeHtml(value.slice(lastIndex)));
  }

  return parts.join("");
};

const chipHtmlToValue = (container: HTMLElement): string => {
  let nextValue = "";

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      nextValue += node.textContent ?? "";
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    const variableName = element.getAttribute("data-var");
    if (variableName !== null) {
      nextValue += `{{${variableName}}}`;
      return;
    }

    if (element.tagName === "BR") return;
    element.childNodes.forEach(walk);
  };

  container.childNodes.forEach(walk);
  return nextValue
    .replaceAll(ZERO_WIDTH_SPACE, "")
    .replace(/\r?\n/g, "")
    .replace(/\u00a0/g, " ");
};

const createInputChangeEvent = (
  nextValue: string,
): React.ChangeEvent<HTMLInputElement> =>
  ({
    target: { value: nextValue },
    currentTarget: { value: nextValue },
  }) as React.ChangeEvent<HTMLInputElement>;

const findAdjacentChip = (
  editor: HTMLElement,
  direction: "previous" | "next",
): HTMLElement | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;

  const findChipSibling = (
    node: Node | null,
    siblingDirection: "previous" | "next",
  ): HTMLElement | null => {
    let current = node;
    while (current) {
      if (current instanceof HTMLElement && current.hasAttribute("data-var")) {
        return current;
      }
      if (
        current.nodeType !== Node.TEXT_NODE ||
        (current.textContent ?? "").replaceAll(ZERO_WIDTH_SPACE, "").trim() !==
          ""
      ) {
        return null;
      }
      current =
        siblingDirection === "previous"
          ? current.previousSibling
          : current.nextSibling;
    }
    return null;
  };

  let candidate: HTMLElement | null = null;
  const { startContainer, startOffset } = range;

  if (startContainer === editor) {
    candidate = findChipSibling(
      direction === "previous"
        ? editor.childNodes[startOffset - 1]
        : editor.childNodes[startOffset],
      direction,
    );
  } else if (startContainer.nodeType === Node.TEXT_NODE) {
    const text = startContainer.textContent ?? "";
    const beforeCursor = text.slice(0, startOffset);
    const afterCursor = text.slice(startOffset);
    if (
      direction === "previous" &&
      beforeCursor.replaceAll(ZERO_WIDTH_SPACE, "") === ""
    ) {
      candidate = findChipSibling(startContainer.previousSibling, "previous");
    }
    if (
      direction === "next" &&
      afterCursor.replaceAll(ZERO_WIDTH_SPACE, "") === ""
    ) {
      candidate = findChipSibling(startContainer.nextSibling, "next");
    }
  }

  return candidate;
};

const placeCursorAtEnd = (container: HTMLElement): void => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const placeCursorAroundNode = (
  node: Node,
  position: "before" | "after",
): void => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  if (position === "before") {
    range.setStartBefore(node);
  } else {
    range.setStartAfter(node);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const insertTextAtSelection = (container: HTMLElement, text: string): void => {
  const selection = window.getSelection();
  if (!selection) return;

  if (selection.rangeCount === 0 || !container.contains(selection.anchorNode)) {
    placeCursorAtEnd(container);
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

// ============================================
// Shared Components
// ============================================
interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  tooltip?: React.ReactNode;
  tooltipPlacement?: TooltipPlacement;
  tooltipVariant?: TooltipVariant;
}

const Label: React.FC<LabelProps> = ({
  children,
  required,
  htmlFor,
  tooltip,
  tooltipPlacement = "top",
  tooltipVariant = "default",
}) => (
  <label
    htmlFor={htmlFor}
    className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
    style={{ color: colors.textDefault }}
  >
    <span>{children}</span>
    {required && <span style={{ color: colors.error }}>*</span>}
    {tooltip && (
      <Tooltip
        title={tooltip}
        placement={tooltipPlacement}
        variant={tooltipVariant}
      >
        <span
          className="inline-flex items-center cursor-help"
          style={{ color: colors.textMuted }}
        >
          <Info size={14} />
        </span>
      </Tooltip>
    )}
  </label>
);

interface HelperTextProps {
  children: React.ReactNode;
  isError?: boolean;
}

const HelperText: React.FC<HelperTextProps> = ({ children, isError }) => (
  <div
    className="mt-1 text-xs"
    style={{ color: isError ? colors.error : colors.textMuted }}
  >
    {children}
  </div>
);

// ============================================
// Input Component
// ============================================

export const Input = React.forwardRef<InputRef, InputProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      size = "medium",
      variant = "filled",
      fullWidth = true,
      type = "text",
      placeholder,
      disabled = false,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      prefix,
      suffix,
      allowClear = false,
      className = "",
      maxLength,
      readOnly = false,
      style,
      tooltip,
      tooltipPlacement,
      tooltipVariant,
      onPressEnter,
      ...props
    },
    ref
  ) => {
    const chipEditorRef = React.useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [keepChipEditor, setKeepChipEditor] = React.useState(false);
    const { currentStringValue, isFilled, setIfUncontrolled } =
      useStringFieldValue(value, defaultValue);
    const hasVariables = hasVariableToken(currentStringValue);
    const showMaskedValue =
      type === "text" && (hasVariables || keepChipEditor) && !disabled;
    const chipEditorReadOnly = readOnly || disabled;

    React.useLayoutEffect(() => {
      const editor = chipEditorRef.current;
      if (!editor) return;

      if (document.activeElement !== editor) {
        editor.innerHTML = valueToChipHtml(currentStringValue);
      }

      if (showMaskedValue && isFocused && document.activeElement !== editor) {
        requestAnimationFrame(() => {
          editor.focus();
          placeCursorAtEnd(editor);
        });
      }
    }, [currentStringValue, isFocused, showMaskedValue]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIfUncontrolled(e.target.value);
      onChange?.(e);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      e.stopPropagation();
    };

    const updateChipValue = (nextValue: string) => {
      const limitedValue =
        typeof maxLength === "number"
          ? nextValue.slice(0, maxLength)
          : nextValue;
      setIfUncontrolled(limitedValue);
      onChange?.(createInputChangeEvent(limitedValue));
      return limitedValue;
    };

    const handleChipFocus = (e: React.FocusEvent<HTMLDivElement>) => {
      setKeepChipEditor(true);
      setIsFocused(true);
      requestAnimationFrame(() => {
        const selection = window.getSelection();
        const editor = e.currentTarget;
        if (
          !selection ||
          selection.rangeCount === 0 ||
          !editor.contains(selection.anchorNode)
        ) {
          placeCursorAtEnd(editor);
        }
      });
      onFocus?.(e as unknown as React.FocusEvent<HTMLInputElement>);
    };

    const handleChipBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      const nextValue = chipHtmlToValue(e.currentTarget);
      setKeepChipEditor(hasVariableToken(nextValue));
      setIsFocused(false);
      onBlur?.(e as unknown as React.FocusEvent<HTMLInputElement>);
    };

    const handleChipInput = (e: React.FormEvent<HTMLDivElement>) => {
      updateChipValue(chipHtmlToValue(e.currentTarget));
    };

    const handleChipKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e as unknown as React.KeyboardEvent<HTMLInputElement>);
      e.stopPropagation();
      if (e.defaultPrevented) return;

      if (e.key === "Enter") {
        e.preventDefault();
        onPressEnter?.(e as unknown as React.KeyboardEvent<HTMLInputElement>);
        return;
      }

      if (e.key === " " || e.key === "Spacebar" || e.code === "Space") {
        e.preventDefault();
        insertTextAtSelection(e.currentTarget, " ");
        updateChipValue(chipHtmlToValue(e.currentTarget));
        return;
      }

      const direction =
        e.key === "Backspace" ? "previous" : e.key === "Delete" ? "next" : null;
      if (!direction) return;

      const chip = findAdjacentChip(e.currentTarget, direction);
      if (!chip) return;

      e.preventDefault();
      const parent = chip.parentNode;
      const nextSibling = chip.nextSibling;
      chip.remove();
      updateChipValue(chipHtmlToValue(e.currentTarget));

      if (parent) {
        const selection = window.getSelection();
        const range = document.createRange();
        if (nextSibling && parent.contains(nextSibling)) {
          range.setStartBefore(nextSibling);
        } else {
          range.setStart(parent, parent.childNodes.length);
        }
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const handleChipPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      insertTextAtSelection(e.currentTarget, text.replace(/\r?\n/g, " "));
      updateChipValue(chipHtmlToValue(e.currentTarget));
    };

    const handleChipMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const chip = target.closest<HTMLElement>("[data-var]");
      if (!chip || !e.currentTarget.contains(chip)) return;

      e.preventDefault();
      e.currentTarget.focus();
      const rect = chip.getBoundingClientRect();
      placeCursorAroundNode(
        chip,
        e.clientX < rect.left + rect.width / 2 ? "before" : "after",
      );
    };

    const inputStyle = createFieldStyle({
      size,
      variant,
      isActive: isFocused,
      isFilled,
      disabled,
      hasError: Boolean(error),
      style,
    });
    const chipInputStyle: React.CSSProperties = {
      ...inputStyle,
      display: "block",
      width: "100%",
      paddingLeft: prefix ? 36 : size === "large" ? 16 : 12,
      paddingRight:
        getInlineAffixWidth(suffix) +
        (allowClear ? 24 : 0) +
        (size === "large" ? 16 : 12),
      borderStyle: "solid",
      color: style?.color ?? colors.textDarkest,
      caretColor: style?.caretColor ?? colors.textDarkest,
      cursor: chipEditorReadOnly ? "not-allowed" : "text",
      outline: "none",
      overflow: "hidden",
      whiteSpace: "pre",
      boxSizing: "border-box",
      lineHeight: sizeConfig[size].height,
    };

    return (
      <div className={cn(fullWidth && "w-full", className)}>
        {label && (
          <Label
            required={required}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            tooltipVariant={tooltipVariant}
          >
            {label}
          </Label>
        )}
        {showMaskedValue ? (
          <div className="relative">
            {prefix && (
              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                {prefix}
              </span>
            )}
            <div
              ref={chipEditorRef}
              id={props.id}
              contentEditable={!chipEditorReadOnly}
              role="textbox"
              aria-disabled={chipEditorReadOnly}
              aria-label={typeof placeholder === "string" ? placeholder : label}
              suppressContentEditableWarning
              tabIndex={chipEditorReadOnly && disabled ? -1 : 0}
              spellCheck={false}
              style={chipInputStyle}
              onFocus={handleChipFocus}
              onBlur={handleChipBlur}
              onInput={handleChipInput}
              onKeyDown={handleChipKeyDown}
              onMouseDown={handleChipMouseDown}
              onPaste={handleChipPaste}
            />
            {!currentStringValue && placeholder && (
              <span
                className="pointer-events-none absolute top-1/2 -translate-y-1/2"
                style={{
                  left: prefix ? 36 : size === "large" ? 16 : 12,
                  color: colors.textWeak,
                  fontSize: inputStyle.fontSize,
                }}
              >
                {placeholder}
              </span>
            )}
            {suffix && (
              <span
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm leading-none whitespace-nowrap"
                style={{ color: colors.textMuted }}
              >
                {suffix}
              </span>
            )}
          </div>
        ) : (
          <AntInput
            ref={ref}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleInputKeyDown}
            prefix={prefix}
            suffix={suffix}
            allowClear={allowClear}
            maxLength={maxLength}
            onPressEnter={onPressEnter}
            readOnly={readOnly}
            variant={variant}
            status={error ? "error" : undefined}
            style={inputStyle}
            {...props}
          />
        )}
        {helperText && !error && <HelperText>{helperText}</HelperText>}
        {error && <HelperText isError>{error}</HelperText>}
      </div>
    );
  }
);

Input.displayName = "Input";

// ============================================
// PasswordInput Component
// ============================================

export const PasswordInput = React.forwardRef<InputRef, PasswordInputProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      size = "medium",
      variant = "filled",
      fullWidth = true,
      placeholder,
      disabled = false,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      prefix,
      allowClear = false,
      visibilityToggle = true,
      className = "",
      readOnly = false,
      style,
      tooltip,
      tooltipPlacement,
      tooltipVariant,
      onPressEnter,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const { isFilled, setIfUncontrolled } = useStringFieldValue(
      value,
      defaultValue,
    );

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIfUncontrolled(e.target.value);
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      e.stopPropagation();
    };

    const inputStyle = createFieldStyle({
      size,
      variant,
      isActive: isFocused,
      isFilled,
      disabled,
      hasError: Boolean(error),
      style,
    });

    return (
      <div className={cn(fullWidth && "w-full", className)}>
        {label && (
          <Label
            required={required}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            tooltipVariant={tooltipVariant}
          >
            {label}
          </Label>
        )}
        <Password
          ref={ref}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          prefix={prefix}
          allowClear={allowClear}
          onPressEnter={onPressEnter}
          readOnly={readOnly}
          variant={variant}
          visibilityToggle={visibilityToggle}
          iconRender={(visible) =>
            visible ? (
              <EyeOutlined style={{ color: colors.textMuted }} />
            ) : (
              <EyeInvisibleOutlined style={{ color: colors.textMuted }} />
            )
          }
          status={error ? "error" : undefined}
          style={inputStyle}
          {...props}
        />
        {helperText && !error && <HelperText>{helperText}</HelperText>}
        {error && <HelperText isError>{error}</HelperText>}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

// ============================================
// PhoneInput Component
// ============================================

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  required = false,
  helperText,
  error,
  size = "large",
  fullWidth = true,
  placeholder = "Enter phone number",
  disabled = false,
  value = "",
  countryCode,
  defaultCountryCode = "+91",
  onChange,
  onCountryChange,
  countries,
  className = "",
  searchable = true,
  renderFlag,
  onKeyDown,
  autoFocus = false,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [dropdownPos, setDropdownPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRowRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = React.useRef<InputRef>(null);

  // Auto focus effect
  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const selectedCountryCode = countryCode || defaultCountryCode;
  const selectedCountry =
    countries.find((c) => c.dial_code === selectedCountryCode) || countries[0];

  const filteredCountries = React.useMemo(() => {
    if (!searchQuery) return countries;
    const query = searchQuery.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dial_code.includes(query) ||
        country.code.toLowerCase().includes(query)
    );
  }, [countries, searchQuery]);

  // Reset highlight when filtered list changes
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredCountries.length, searchQuery]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Ref for portal dropdown element
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click (check both container and portal dropdown)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setIsDropdownOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate and update dropdown position when open
  React.useEffect(() => {
    if (!isDropdownOpen || !inputRowRef.current) {
      setDropdownPos(null);
      return;
    }
    const updatePos = () => {
      if (!inputRowRef.current) return;
      const rect = inputRowRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [isDropdownOpen]);

  const handleCountrySelect = React.useCallback(
    (country: PhoneInputCountry) => {
      onCountryChange?.(country);
      setIsDropdownOpen(false);
      setSearchQuery("");
      setHighlightedIndex(-1);
    },
    [onCountryChange],
  );

  // Keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isDropdownOpen) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) =>
            prev < filteredCountries.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCountries.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredCountries.length
          ) {
            handleCountrySelect(filteredCountries[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setIsDropdownOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
          break;
      }
    },
    [isDropdownOpen, filteredCountries, highlightedIndex, handleCountrySelect]
  );

  const isFilled = Boolean(value && value.length > 0);
  const isActive = isFocused || isDropdownOpen;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, "");
    onChange?.(numericValue);
  };

  const handlePhoneNumberKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    onKeyDown?.(e);
    e.stopPropagation();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyDown(e);
    e.stopPropagation();
  };

  // Default flag renderer using emoji
  const defaultRenderFlag = (code: string) => {
    const codePoints = code
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return (
      <span className="text-lg">{String.fromCodePoint(...codePoints)}</span>
    );
  };

  const flagRenderer = renderFlag || defaultRenderFlag;

  const containerStyle: React.CSSProperties = {
    borderColor: getFieldBorderColor({
      hasError: Boolean(error),
      isActive,
      isFilled,
    }),
    borderWidth: isActive || error ? 2 : 1,
    backgroundColor: getFieldBackgroundColor({
      disabled,
      isActive,
      variant: "filled",
    }),
    borderRadius: 3,
    borderStyle: "solid",
    transition: FIELD_TRANSITION,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "text",
  };

  return (
    <div className={cn(fullWidth && "w-full", className)} ref={containerRef}>
      {label && <Label required={required}>{label}</Label>}

      <div
        ref={inputRowRef}
        className="flex items-center relative"
        style={{ ...containerStyle, height: sizeConfig[size].height }}
      >
        {/* Country Code Selector */}
        <div
          className={cn(
            "flex items-center gap-1.5 h-full shrink-0 select-none",
            sizeConfig[size].px,
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          style={{ borderRight: `1px solid ${colors.surfaceSubtle}` }}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setIsDropdownOpen(!isDropdownOpen);
          }}
        >
          {selectedCountry && flagRenderer(selectedCountry.code)}
          <span
            className="font-medium"
            style={{
              color: colors.textInverse,
              fontSize: sizeConfig[size].fontSize,
            }}
          >
            {selectedCountry?.dial_code}
          </span>
          <ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-200",
              isDropdownOpen && "rotate-180"
            )}
            style={{ color: colors.textMuted }}
          />
        </div>

        {/* Phone Number Input */}
        <AntInput
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handlePhoneNumberKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="borderless"
          className="flex-1 h-full"
          style={{
            color: colors.textInverse,
            backgroundColor: "transparent",
            fontSize: sizeConfig[size].fontSize,
          }}
        />

        {/* Dropdown rendered via portal to avoid modal overflow clipping */}
        {isDropdownOpen && dropdownPos && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="ds-phone-dropdown-portal rounded-md overflow-hidden"
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 2000,
              backgroundColor: colors.white,
              border: `1px solid ${colors.surfaceSubtle}`,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              maxHeight: searchable ? 262 : 210,
              overflowY: "auto",
            }}
          >
            {searchable && (
              <div
                className="sticky top-0 p-2"
                style={{
                  backgroundColor: colors.white,
                  borderBottom: `1px solid ${colors.surfaceSubtle}`,
                }}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded"
                  style={{
                    border: `1px solid ${colors.surfaceSubtle}`,
                    backgroundColor: colors.white,
                  }}
                >
                  <Search
                    size={16}
                    className="shrink-0"
                    style={{ color: colors.textMuted }}
                  />
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 border-none outline-none text-sm bg-transparent"
                    style={{ color: colors.textInverse }}
                    autoFocus
                  />
                </div>
              </div>
            )}

            {filteredCountries.map((country, index) => {
              const isSelected =
                country.dial_code === selectedCountry?.dial_code;
              const isHighlighted = index === highlightedIndex;

              let bgColor = "transparent";
              if (isHighlighted) bgColor = colors.surfaceLighter;
              else if (isSelected) bgColor = colors.primaryExtraLight;

              return (
                <div
                  key={`${country.code}-${country.dial_code}`}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-150"
                  style={{ backgroundColor: bgColor }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => {
                    if (highlightedIndex === index) setHighlightedIndex(-1);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCountrySelect(country);
                  }}
                >
                  {flagRenderer(country.code)}
                  <span
                    className="flex-1 text-sm"
                    style={{ color: colors.textInverse }}
                  >
                    {country.name}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.textMuted }}
                  >
                    {country.dial_code}
                  </span>
                </div>
              );
            })}

            {filteredCountries.length === 0 && (
              <div
                className="py-4 px-3 text-center text-sm"
                style={{ color: colors.textMuted }}
              >
                No countries found
              </div>
            )}
          </div>,
          document.body,
        )}
      </div>

      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {error && <HelperText isError>{error}</HelperText>}
    </div>
  );
};

PhoneInput.displayName = "PhoneInput";

// ============================================
// TextAreaInput Component
// ============================================

export const TextAreaInput: React.FC<TextAreaProps> = ({
  label,
  required = false,
  helperText,
  error,
  size = "medium",
  variant = "filled",
  fullWidth = true,
  placeholder,
  disabled = false,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  autoFocus = false,
  rows = 4,
  autoSize,
  maxLength,
  showCount = false,
  countInside = false,
  className = "",
  id,
  borderless = false,
  suffix,
  wrapperStyle,
  wrapperClassName = "",
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const { currentStringValue, isFilled, setIfUncontrolled } =
    useStringFieldValue(value, defaultValue);
  const currentLength = currentStringValue.length;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIfUncontrolled(e.target.value);
    onChange?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(e);
    e.stopPropagation();
  };

  const shouldShowCountInside = Boolean(showCount && countInside && maxLength);
  const shouldShowCountOutside = showCount && !countInside;
  const currentBorderWidth = borderless ? 0 : isFocused || error ? 2 : 1;
  const borderColor = getFieldBorderColor({
    borderless,
    hasError: Boolean(error),
    isActive: isFocused,
    isFilled,
  });
  const backgroundColor = getFieldBackgroundColor({
    disabled,
    isActive: isFocused,
    variant,
  });

  const textAreaStyle: React.CSSProperties = {
    ...createFieldStyle({
      size,
      variant,
      isActive: isFocused,
      isFilled,
      disabled,
      hasError: Boolean(error),
      borderless: borderless || shouldShowCountInside,
      includeHeight: false,
    }),
    borderRadius: shouldShowCountInside ? 0 : 3,
    ...(shouldShowCountInside && {
      paddingBottom: 28,
      resize: "vertical",
    }),
  };

  const containerStyle: React.CSSProperties = {
    position: "relative",
    ...(shouldShowCountInside && {
      borderColor,
      borderWidth: currentBorderWidth,
      borderStyle: "solid",
      borderRadius: 3,
      overflow: "hidden",
      backgroundColor,
      transition: FIELD_TRANSITION,
    }),
    ...wrapperStyle,
  };

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      {label && <Label required={required}>{label}</Label>}
      <div className={wrapperClassName} style={containerStyle}>
        <TextArea
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          rows={rows}
          autoSize={autoSize}
          maxLength={maxLength}
          showCount={shouldShowCountOutside}
          status={error ? "error" : undefined}
          style={textAreaStyle}
        />
        {shouldShowCountInside && (
          <div
            className="absolute bottom-1 right-2 px-1 text-sm leading-5 pointer-events-none"
            style={{
              backgroundColor,
              color: colors.textMuted,
            }}
          >
            {currentLength} / {maxLength}
          </div>
        )}
      </div>
      {suffix && <div className="mt-1">{suffix}</div>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {error && <HelperText isError>{error}</HelperText>}
    </div>
  );
};

TextAreaInput.displayName = "TextAreaInput";

// ============================================
// InputCountSuffix
// Reusable character count for Input `suffix` (or TextArea `suffix`).
// Renders "{current} / {max}" in a compact, muted style so it reads as
// metadata rather than content.
// ============================================

export interface InputCountSuffixProps {
  current: number;
  max: number;
  className?: string;
}

export const InputCountSuffix: React.FC<InputCountSuffixProps> = ({
  current,
  max,
  className,
}) => (
  <span
    className={cn("text-sm leading-none whitespace-nowrap", className)}
    style={{ color: colors.textMuted }}
  >
    {current} / {max}
  </span>
);

InputCountSuffix.displayName = "InputCountSuffix";

export default Input;
